import React from 'react';
import { FunctionDataType } from '@glueapp/graph-types';
import { Button } from '@/components/migrated/Button';
import { Edit2, Trash2, Wrench } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CompatibilityInfo } from './functionCompatibility';
import { TypeSignatureDisplay } from './TypeSignatureDisplay';
import { MissingPortsInfo } from './MissingPortsInfo';

interface FunctionItemProps {
    info: CompatibilityInfo;
    isSelected: boolean;
    onSelect: () => void;
    onEdit?: () => void;
    onDelete?: () => void;
    onFix?: () => void;
    expectedType: FunctionDataType;
}

export const FunctionItem: React.FC<FunctionItemProps> = ({ info, isSelected, onSelect, onEdit, onDelete, onFix, expectedType }) => {
    const canFix =
        !info.compatible &&
        (info.missingInputs.length > 0 ||
            info.missingOutputs.length > 0 ||
            info.incompatibleInputs.length > 0 ||
            info.incompatibleOutputs.length > 0);

    return (
        <div
            className={cn(
                'p-3 rounded-lg border transition-colors',
                isSelected
                    ? 'bg-primary/10 border-primary'
                    : info.compatible
                      ? 'bg-card border-border hover:border-primary/50 cursor-pointer'
                      : 'bg-muted/30 border-border',
            )}
            onClick={info.compatible && !isSelected ? onSelect : undefined}
        >
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="font-medium">{info.function.name}</span>
                <div className="flex items-center gap-2">
                    {isSelected && <span className="text-xs px-1.5 py-0.5 rounded bg-primary text-primary-foreground">Selected</span>}
                    {info.compatible ? (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300">
                            Compatible
                        </span>
                    ) : (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300">
                            Incompatible
                        </span>
                    )}
                </div>
            </div>

            <div className="flex items-center justify-between gap-2 mt-2">
                <div className="flex items-center gap-2">
                    {onEdit && (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={e => {
                                e.stopPropagation();
                                onEdit();
                            }}
                            className="h-7 text-xs gap-1"
                            title="Edit function"
                        >
                            <Edit2 className="w-3 h-3" />
                            Edit
                        </Button>
                    )}
                </div>
                {canFix && onFix && (
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={e => {
                            e.stopPropagation();
                            onFix();
                        }}
                        className="h-7 text-xs gap-1"
                        title="Adds any missing ports and changes types of incompatible ports"
                    >
                        <Wrench className="w-3 h-3" />
                        Make Compatible
                    </Button>
                )}
            </div>

            {!info.compatible && <MissingPortsInfo missingInputs={info.missingInputs} missingOutputs={info.missingOutputs} />}

            <TypeSignatureDisplay func={info.function} expectedType={expectedType} />

            {onDelete && (
                <div className="flex justify-end mt-2">
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={e => {
                            e.stopPropagation();
                            onDelete();
                        }}
                        className="h-7 text-xs gap-1 text-destructive hover:text-destructive"
                        title="Delete function"
                    >
                        <Trash2 className="w-3 h-3" />
                        Delete
                    </Button>
                </div>
            )}
        </div>
    );
};
